import {
  Card, CardContent,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip
} from '@mui/material'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import React, { useState } from 'react'
import { type Address, type Phone, type State, type PersonAddress, type Country, type Region } from 'services'
import {
  StateSelect,
  PhoneTextField,
  CountryAddressSelector,
  fullPhone,
  PhoneExtensionTextField,
  useCountriesLuts
  , RegionAutoComplete
} from 'components'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { createPersonPhoneWithNullProperties } from 'helpers'
import { SessionRegion, useSessionContext } from 'session'

interface PersonAdressCardProps {
  personAddress: PersonAddress
  region: Region | null
  countryId: number | null
  index: number
  updateRegion: (newRegion: Region | null) => void
  updateAddress: (newAddress: PersonAddress) => void
  startExpanded: boolean
  disabled: boolean
}
export const PersonAddressBlock: React.FC<PersonAdressCardProps> = ({ personAddress, index, updateAddress, startExpanded = false, disabled, region, countryId, updateRegion }) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [expanded, setExpanded] = useState(startExpanded)
  const { lutValues: countriesLutValues } = useCountriesLuts()
  const { sessionData } = useSessionContext()
  const secondaryAddress = personAddress?.secondaryAddress
  const primaryAddress = personAddress?.primaryAddress

  const foundCountry = (): Country | null => {
    if ((personAddress?.primaryAddress?.country) != null) {
      if (personAddress.primaryAddress.country.country_desc !== null) {
        return personAddress.primaryAddress.country
      }
    }

    return countriesLutValues.countries.find(lutCountry => lutCountry.id === personAddress.primaryAddress?.country?.id) ?? null
  }

  const [countryDesc, setCountryDesc] = useState<string | null>(foundCountry()?.country_desc ?? null)

  const handleExpandClick = (): void => {
    setExpanded(!expanded)
  }

  const handlePhoneChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    filterKey: keyof Phone
  ): void => {
    const value = event.target.value
    const clearedExtension = value === '' ? '' : (personAddress.phone?.extension ?? '')

    if (personAddress.phone != null) {
      updateAddress({ ...personAddress, phone: { ...personAddress.phone, id: personAddress.phone.id ?? 0, phoneNumber: value, countryCode: '', extension: clearedExtension } })
    } else {
      updateAddress({ ...personAddress, phone: { id: 0, addressId: personAddress.id ?? null, personId: personAddress.personId, phoneNumber: value, countryCode: '', extension: '' } })
    }
  }

  const handlePhoneExtensionChange = (
    newValue: string
  ): void => {
    if (personAddress.phone != null) { updateAddress({ ...personAddress, phone: { ...personAddress.phone, extension: newValue } }) }
  }

  const handleTabChange = (_event: any, newValue: React.SetStateAction<number>): void => {
    if (newValue === 1 && secondaryAddress == null) {
      updateAddress({ ...personAddress, secondaryAddress: { id: undefined, addressOne: '', addressTwo: '', city: '', state: null, postalCode: '', country: personAddress.primaryAddress.country, mailingAddress: false } })
    }
    setSelectedTab(newValue)
  }

  const changeSecondaryValue = (filterKey: string, value: any): void => {
    const newSecondary = {
      ...secondaryAddress,
      [filterKey]: value
    }
    updateAddress({ ...personAddress, secondaryAddress: newSecondary })
  }

  const changePrimaryValue = (filterKey: string, value: any): void => {
    const newPrimary = {
      ...primaryAddress,
      [filterKey]: value
    }
    updateAddress({ ...personAddress, primaryAddress: newPrimary })
  }
  const handleChangeAddressTextField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    filterKey: keyof Address,
    isSecondary: boolean = false
  ): void => {
    const value = event.target.value
    if (isSecondary) {
      changeSecondaryValue(filterKey, value)
    } else {
      changePrimaryValue(filterKey, value)
    }
  }

  const renderAddressTextField = (
    label: string,
    filterKey: keyof Address,
    value: string = '',
    isSecondary: boolean = false
  ): JSX.Element => (
        <div>
            <TextField
                label={label}
    variant="outlined"
    disabled={disabled}
    value={value}
    onChange={(e) => {
      if (isSecondary) {
        handleChangeAddressTextField(e, filterKey, true)
      } else {
        handleChangeAddressTextField(e, filterKey, false)
      }
    }}
    fullWidth
    />
    {!isSecondary && filterKey !== 'addressTwo' && value === '' && <FormHelperText>This field is required</FormHelperText>}
    {isSecondary && filterKey !== 'addressTwo' && !isAllFieldsSecondaryFilled() && value === '' && <FormHelperText>This field is required</FormHelperText>}
    </div>
  )

  const renderSelectState = (
    state: State | null,
    isSecondary: boolean = false,
    country: Country | null
  ): JSX.Element => (
            <div>
                <StateSelect disabled={disabled} country={country} state={state} handleStateChange={(e) => {
                  if (!isSecondary) {
                    handleStatePrimaryChange(e)
                  } else {
                    handleStateSecondaryChange(e)
                  }
                }} />
            </div>
  )

  const handleChangeControlLabel = (
    filterKey: keyof Address | keyof Phone,
    value: boolean | string,
    isSecondary: boolean = false
  ): void => {
    if (isSecondary) {
      changeSecondaryValue(filterKey, value)
      if (primaryAddress.mailingAddress) {
        changePrimaryValue(filterKey, false)
      }
    } else {
      changePrimaryValue(filterKey, value)
      if (secondaryAddress.mailingAddress) {
        changeSecondaryValue(filterKey, false)
      }
    }
  }

  const isAllFieldsPrimaryFilled = (): boolean => {
    if (
      primaryAddress.addressOne === '' ||
                        primaryAddress.city === '' ||
                        primaryAddress?.state?.id === 0 ||
                        primaryAddress?.state?.name === '' ||
                        primaryAddress.postalCode === '') {
      return false
    }

    return true
  }

  const isAllFieldsSecondaryNull = (): boolean => {
    if (secondaryAddress === null) {
      return true
    }
    if (
      (secondaryAddress?.addressOne === null || (secondaryAddress?.addressOne)?.length === 0) &&
                        (secondaryAddress?.city === null || (secondaryAddress?.city)?.length === 0) &&
                        ((secondaryAddress?.state) == null) &&
                        (secondaryAddress?.postalCode === null || (secondaryAddress?.postalCode)?.length === 0)
    ) {
      return true
    }

    return false
  }

  const isAllFieldsSecondaryFilled = (): boolean => {
    const result = isAllFieldsSecondaryNull()
    if (result) {
      return result
    }

    if (
      ((secondaryAddress?.addressOne)?.length === 0) ||
                        ((secondaryAddress?.city)?.length === 0) ||
                        ((secondaryAddress?.state) == null) ||
                        ((secondaryAddress?.postalCode)?.length === 0)
    ) {
      return false
    }
    return true
  }

  const handleStatePrimaryChange = (state: State | null): void => {
    changePrimaryValue('state', state)
  }

  const handleStateSecondaryChange = (state: State | null): void => {
    changeSecondaryValue('state', state)
  }

  const getRegionLabel = (): string => countryId != null ? 'Region Represented' : 'Country Represented'

  return (<Card>
     <CardContent sx={{ px: 1, pt: 0, '&:last-child': { pb: expanded ? 'default' : 1 } }}>
      <Grid container alignItems={'center'} marginTop={1}>
        {!expanded &&
        <IconButton onClick={handleExpandClick} aria-expanded={expanded} aria-label="show more">
          <ExpandMoreIcon />
        </IconButton>
        }
        {expanded &&
        <IconButton onClick={handleExpandClick} aria-expanded={expanded} aria-label="show less">
          <ExpandLessIcon />
        </IconButton>
        }
        <Typography variant="body1">
          {!expanded && `${personAddress.primaryAddress.addressOne} ${personAddress.primaryAddress.addressTwo ?? ''} - ${personAddress.primaryAddress.city} - ${
              personAddress.primaryAddress.state?.name ?? 'N/A'
          }`}
        </Typography>
      </Grid>
    <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div>
          <Tabs value={selectedTab} onChange={handleTabChange} id={`simple-tabs-${index}`}>
            <Tab label="Address 1" id={`simple-tab1-${index}`} />
            <Tab label="Address 2" id={`simple-tab2-${index}`} />
          </Tabs>
          <br />
          <FormControl fullWidth>
            <Grid container spacing={5}>
              <Grid item xs={4}>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <PhoneTextField label={'Phone Number'} filterKey={'phoneNumber'} value={fullPhone(personAddress.phone?.countryCode, personAddress.phone?.phoneNumber)} disabled={disabled} countryDesc={countryDesc} handlePhoneChange={ handlePhoneChange }/>
                  </Grid>
                  <Grid item xs={4}>
                    <PhoneExtensionTextField label={'Extension'} value={personAddress.phone?.extension} disabled={disabled} handlePhoneExtensionChange={(ext) => { handlePhoneExtensionChange(ext) }} phone={personAddress.phone}/>
                  </Grid>
                </Grid>
              </Grid>
              {sessionData.region === SessionRegion.INTL &&
                <Grid item xs={6}>
                  <RegionAutoComplete label={getRegionLabel()} disabled={disabled} region={region} setRegion={updateRegion} positionCountryId={countryId}></RegionAutoComplete>
                </Grid>
              }
            </Grid>
          </FormControl>

          {selectedTab === 0 && (
              <Typography component="div" role="tabpanel" hidden={selectedTab !== 0}>
                <br />
                <FormControl fullWidth error={!isAllFieldsPrimaryFilled() && !disabled}>
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      {renderAddressTextField('Address 1', 'addressOne', primaryAddress.addressOne)}
                    </Grid>
                    <Grid item xs={5}>
                      {renderAddressTextField('Address 2', 'addressTwo', primaryAddress.addressTwo)}
                    </Grid>
                    <Grid item xs={1}>
                      <FormControlLabel
                          control={<Switch
                              disabled={disabled}
                              checked={primaryAddress.mailingAddress}
                              onChange={(e) => { handleChangeControlLabel('mailingAddress', !primaryAddress.mailingAddress, false) }}
                              inputProps={{ 'aria-label': 'controlled' }}
                          />}
                          label="Mailing Address"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <CountryAddressSelector countries={countriesLutValues.countries} value={personAddress.primaryAddress?.country} setValue={(country) => {
                        if (country?.country_desc !== countryDesc) {
                          setCountryDesc(country?.country_desc ?? null)
                        }
                        updateAddress({
                          ...personAddress,
                          primaryAddress: { ...personAddress.primaryAddress, country },
                          phone: country?.id !== primaryAddress?.country?.id
                            ? createPersonPhoneWithNullProperties(personAddress.personId)
                            : personAddress.phone
                        })
                      }} disabled={disabled}/>
                    </Grid>
                    <Grid item xs={2}>
                      {renderSelectState(primaryAddress?.state, false, personAddress.primaryAddress?.country)}
                    </Grid>
                    <Grid item xs={4}>
                      {renderAddressTextField('City', 'city', primaryAddress.city)}
                    </Grid>
                    <Grid item xs={4}>
                      {renderAddressTextField('Postal Code', 'postalCode', primaryAddress.postalCode)}
                    </Grid>
                  </Grid>
                </FormControl>
              </Typography>
          )}

          {selectedTab === 1 && (
              <Typography component="div" role="tabpanel" hidden={selectedTab !== 1}>
                <br />
                <FormControl fullWidth error={!isAllFieldsPrimaryFilled() && !disabled}>
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      {renderAddressTextField('Address 1', 'addressOne', secondaryAddress?.addressOne === null ? '' : secondaryAddress.addressOne, true)}
                    </Grid>
                    <Grid item xs={5}>
                      {renderAddressTextField('Address 2', 'addressTwo', secondaryAddress?.addressTwo === null ? '' : secondaryAddress.addressTwo, true)}
                    </Grid>
                    <Grid item xs={1}>
                      <Tooltip
                          title={isAllFieldsSecondaryNull() ? 'Please kindly complete the remaining fields' : ''}
                          placement="top">
                        <FormControlLabel
                            control={<Switch
                                disabled={disabled}
                                checked={secondaryAddress?.mailingAddress === null ? false : secondaryAddress.mailingAddress}
                                onChange={(e) => { handleChangeControlLabel('mailingAddress', !secondaryAddress.mailingAddress, true) }}
                                inputProps={{ 'aria-label': 'controlled' }}
                            />}
                            label="Mailing Address"
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={5}>
                      {renderAddressTextField('City', 'city', secondaryAddress?.city === null ? '' : secondaryAddress?.city, true)}
                    </Grid>
                    <Grid item xs={4}>
                      {renderSelectState(secondaryAddress?.state, true, secondaryAddress?.country)}
                    </Grid>
                    <Grid item xs={3}>
                      {renderAddressTextField('Postal Code', 'postalCode', secondaryAddress?.postalCode === null ? '' : secondaryAddress.postalCode, true)}
                    </Grid>
                  </Grid>
                </FormControl>
              </Typography>
          )}
        </div>
    </Collapse>
   </CardContent>
  </Card>

  )
}
